import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  NAME,
  PRIORITY,
  START_DATE,
  END_DATE,
  TASK_STATUS,
  TASK_ATTACHMENT,
  TASKS,
  NOTES,
  ASSIGNEES,
  SCHOOL,
  ROLE,
  ASSIGN_TO,
  TASK_ATTACHMENT_NAME,
} from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import {
  useCreateTaskAssignmentMutation,
  useCreatePredefinedTaskAssignmentMutation,
  useGetTaskAssignmentByIdQuery,
  useUpdateTaskAssignmentMutation,
} from "@/services";
import { useAppDispatch } from "@/redux/hooks";
import { openSnackbarAction } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";

const useAddEdit = () => {
  const { action, id } = useParams<{ action: string; id?: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const attachmentRef = useRef(null);

  const isView = action === routes.view;
  const isEdit = action === routes.edit;

  const [taskType, setTaskType] = useState<"new" | "predefined">("new");
  const [selectedPredefinedTasks, setSelectedPredefinedTasks] = useState<any[]>([]);

  // -----------------------------------
  // Initialize form
  // -----------------------------------
  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [TASKS]: [
        {
          [NAME]: "",
          [PRIORITY]: "",
          [START_DATE]: "",
          [END_DATE]: "",
          [TASK_STATUS]: "",
          [TASK_ATTACHMENT]: "",
          [NOTES]: "",
          [ASSIGNEES]: [
            {
              [SCHOOL]: "",
              [ROLE]: "",
              [ASSIGN_TO]: "",
            },
          ],
        },
      ],
    },
  });

  const requiredFields = getRequiredFields(schema);

  // -----------------------------------
  // API hooks
  // -----------------------------------
  const { mutateAsync: createTaskAssignment, isPending: isCreating } =
    useCreateTaskAssignmentMutation();

  const { mutateAsync: createPredefinedTaskAssignment } =
    useCreatePredefinedTaskAssignmentMutation();

  const { mutateAsync: updateTaskAssignment, isPending: isUpdating } =
    useUpdateTaskAssignmentMutation();

  // Fetch task details (for Edit/View)
  const {
    data: taskDetailData,
    isLoading: isFetching,
  } = useGetTaskAssignmentByIdQuery(id || "", {
    enabled: isEdit || isView,
  });

  // -----------------------------------
  // Populate form when editing
  // -----------------------------------
  useEffect(() => {
    if (taskDetailData?.data && (isEdit || isView)) {
      const t = taskDetailData.data;

      form.reset({
        [TASKS]: [
          {
            [NAME]: t.taskName,
            [PRIORITY]: t.priority,
            [START_DATE]: t.startDate?.split("T")[0],
            [END_DATE]: t.endDate?.split("T")[0],
            [TASK_STATUS]: t.taskStatus,
            [TASK_ATTACHMENT]: "",
            [TASK_ATTACHMENT_NAME]: t.taskAttachmentName || "",
            [NOTES]: t.taskNotes?.[0]?.note || "",
            [ASSIGNEES]: [
              {
                [SCHOOL]: t.schoolData?.[0]?.schoolId || "",
                [ROLE]: t.role,
                [ASSIGN_TO]: t.assignTo,
              },
            ],
          },
        ],
      });
    }
  }, [taskDetailData, isEdit, isView, form]);

  // -----------------------------------
  // Navigation & UI Handlers
  // -----------------------------------
  const onBackClick = () => navigate(-1);

 const onSelectPredefinedTask = (task: any) => {
  setSelectedPredefinedTasks((prev) => {
    if (prev.some((p) => p._id === task._id)) {
      return prev.filter((p) => p._id !== task._id);
    } else {
      return [...prev, task];
    }
  });
};

const onRemovePredefinedTask = (task: any) => {
  setSelectedPredefinedTasks((prev) => prev.filter((p) => p._id !== task._id));
};


  // -----------------------------------
  // Submit Handler
  // -----------------------------------
  const onSubmit = async (data: formValues) => {
  try {
    const cleanedData = setEmptyFieldsToNull(removeEmptyFields(data));
    const firstTask = cleanedData[TASKS][0];

    if (taskType === "predefined" && !data.selectedCategory) {
      form.setError("selectedCategory", {
        message: "Category is required for predefined tasks",
      });
      return;
    }

    if (taskType === "predefined" && selectedPredefinedTasks.length === 0) {
  const errorMessage = "Please select at least one predefined task";

  // Dispatch existing snackbar
  dispatch(
    openSnackbarAction({
      message: errorMessage,
      severity: "error",
    })
  );

  // Also show toast error (consistent with your backend error pattern)
  toast.error(errorMessage);

  return;
}


    // 🧩 EDIT existing task
    if (isEdit && id) {
      const firstAssignee = firstTask[ASSIGNEES]?.[0] || {};
      const payload = {
        id,
        taskName: firstTask[NAME],
        priority: firstTask[PRIORITY],
        taskStatus: firstTask[TASK_STATUS],
        taskAttachment: firstTask[TASK_ATTACHMENT],
        taskNotes: firstTask[NOTES],
        role: firstAssignee[ROLE],
        taskAttachmentName: firstTask[TASK_ATTACHMENT_NAME],
        assignTo: firstAssignee[ASSIGN_TO]
      };

      console.log("🛠 Update Payload:", payload);
      await updateTaskAssignment(payload);
    }

    // 🆕 Create new task
    else if (taskType === "new") {
      const payload = {
        taskName: firstTask[NAME],
        priority: firstTask[PRIORITY],
        startDate: firstTask[START_DATE],
        endDate: firstTask[END_DATE],
        taskStatus: firstTask[TASK_STATUS],
        taskAttachment: firstTask[TASK_ATTACHMENT],
        taskAttachmentName: firstTask[TASK_ATTACHMENT_NAME],
        taskNotes: firstTask[NOTES],
        assignments: firstTask[ASSIGNEES].map((a: any) => ({
          school: a[SCHOOL],
          role: a[ROLE],
          assignTo: a[ASSIGN_TO],
        })),
      };

      console.log("📦 Create Payload (New):", payload);
      await createTaskAssignment(payload);
    }

    // 🧩 Create predefined task(s)
    else if (taskType === "predefined") {
      const commonTask = data?.tasks?.[0] || {};

      const assignments =
        commonTask?.assignees?.map((a: any) => ({
          school: a[SCHOOL],
          role: a[ROLE],
          assignTo: a[ASSIGN_TO],
        })) || [];

      const tasksPayload = selectedPredefinedTasks.map((item: any) => ({
        taskName: item.taskName,
        priority: item.priority,
        taskNotes: item.taskDescription || "",
        startDate: commonTask[START_DATE],
        endDate: commonTask[END_DATE],
        taskStatus: commonTask[TASK_STATUS],
        taskAttachment: commonTask[TASK_ATTACHMENT],
        taskAttachmentName: commonTask[TASK_ATTACHMENT_NAME],
        assignments, // ✅ pass full array
      }));

      console.log("📦 Predefined Payload (Multiple Assignees):", tasksPayload);
      await createPredefinedTaskAssignment(tasksPayload);
    }

    dispatch(
      openSnackbarAction({
        message: isEdit ? "Task updated successfully!" : "Task(s) created successfully!",
        severity: "success",
      })
    );

    queryClient.invalidateQueries({ queryKey: ["taskAssignments"] });
    onBackClick();
  } catch (error: any) {
    console.error("Error submitting task:", error);

    const backendMessage =
      error?.response?.data?.issues?.message || // nested message
      error?.response?.data?.message || // top-level message
      "An error occurred while saving the task"; // fallback

    toast.error(backendMessage);
  }
};


  // -----------------------------------
  // Add/Remove Assignee Handlers
  // -----------------------------------
  const onAddMoreClick = (taskIndex: number) => {
    const tasks = [...form.getValues(TASKS)];
    tasks[taskIndex][ASSIGNEES].push({
      [SCHOOL]: "",
      [ROLE]: "",
      [ASSIGN_TO]: "",
    });
    form.setValue(TASKS, tasks);
  };

  const onRemoveTask = (taskIndex: number, assignIndex: number) => {
    const tasks = [...form.getValues(TASKS)];
    tasks[taskIndex][ASSIGNEES].splice(assignIndex, 1);
    form.setValue(TASKS, tasks);
  };

  // -----------------------------------
  // Return all handlers and state
  // -----------------------------------
  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    attachmentRef,
    onAddMoreClick,
    onRemoveTask,
    isLoading: isCreating || isUpdating || isFetching,
    taskType,
    setTaskType,
    selectedPredefinedTasks,
    onSelectPredefinedTask,
    onRemovePredefinedTask,
  };
};

export default useAddEdit;
